import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { styled as materialStyled } from "@mui/material/styles";
import { Popover, Divider, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export function InfoPopover({
    handleClose,
    anchorEl,
    content = { header: null, body: null },
    horizontalPosition = "center",
    componentStyles = {}
}) {
    return (
        <Popover
            data-testid="info-popover"
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: horizontalPosition
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: horizontalPosition
            }}
        >
            <Content style={componentStyles?.contentContainer}>
                <Header>
                    {content.header}
                    <IconButton onClick={handleClose} aria-label="Close" edge="end" size="large">
                        <CloseIcon />
                    </IconButton>
                </Header>
                <HeaderDivider />
                {content.body}
            </Content>
        </Popover>
    );
}

export function InfoDetail({ children, ...rest }) {
    return (
        <DetailContainer {...rest} tabIndex={0}>
            <InfoIndicator>{children}</InfoIndicator>
            <InfoIcon />
        </DetailContainer>
    );
}

export function useInfoPopover() {
    const [anchorElement, setAnchorElement] = useState(null);
    const [popoverContent, setPopoverContent] = useState({ header: null, body: null });

    const open = (event, content) => {
        setPopoverContent(content);
        setAnchorElement(event.currentTarget);
    };

    const close = () => {
        setAnchorElement(null);
    };

    return [anchorElement, popoverContent, open, close];
}

const DetailContainer = styled.div`
    display: inline-flex;
    align-items: center;
    cursor: pointer;
`;

const InfoIndicator = styled.span`
    text-decoration: underline;
    text-decoration-style: dotted;

    @media print {
        text-decoration: none;
    }
`;

const HeaderDivider = materialStyled(Divider)({
    marginBottom: "1rem"
});

const InfoIcon = materialStyled(InfoOutlinedIcon)({
    fontSize: 12,
    marginLeft: "0.5rem",
    marginTop: "1px"
});

const Header = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Content = styled.div`
    background: white;
    outline: none;
    box-shadow: 2px 2px 5px 0 rgba(0, 0, 0, 0.4);
    padding: 1rem 2rem;

    @media (min-width: 960px) {
        max-width: 750px;
    }
`;

InfoPopover.propTypes = {
    handleClose: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    content: PropTypes.shape({
        header: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
        body: PropTypes.oneOfType([PropTypes.object, PropTypes.node])
    }).isRequired,
    horizontalPosition: PropTypes.oneOf(["left", "center", "right"]),
    componentStyles: PropTypes.shape({
        contentContainer: PropTypes.object
    })
};

InfoDetail.propTypes = {
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.node])
};
